// cellLineSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getApiErrorMessage } from 'src/types/api';

// Cell Line 전체 컬럼 인터페이스
export interface ModelList {
  // 핵심 식별 정보
  model_id?: string;
  sample_id?: string;
  patient_id?: string;
  parent_id?: string;
  model_name?: string;
  synonyms?: string;
  
  // 기본 조직/암 정보
  tissue?: string;
  cancer_type?: string;
  cancer_type_ncit_id?: string;
  tissue_status?: string;
  sample_site?: string;
  cancer_type_detail?: string;
  
  // 모델 특성
  model_type?: string;
  growth_properties?: string;
  model_treatment?: string;
  sampling_day?: string;
  sampling_month?: string;
  sampling_year?: number;
  model_comments?: string;
  model_relations_comment?: string;
  
  // 참조/문헌 정보
  doi?: string;
  pmed?: string;
  
  // 유전자/분자 정보
  msi_status?: string;
  ploidy_snp6?: string;
  ploidy_wes?: string;
  ploidy_wgs?: string;
  mutational_burden?: string;
  
  // 외부 데이터베이스 ID
  COSMIC_ID?: string;
  BROAD_ID?: string;
  CCLE_ID?: string;
  RRID?: string;
  HCMI?: string;
  
  // 공급자 정보
  suppliers?: string;
  supplier?: string;
  cat_number?: string;
  
  // 환자 인구통계
  species?: string;
  gender?: string;
  ethnicity?: string;
  age_at_sampling?: number;
  
  // 환자 병력
  family_history_of_cancer?: string;
  prior_same_malignancy?: string;
  prior_other_malignancy?: string;
  smoking_status?: string;
  alcohol_exposure_intensity?: string;
  alcohol_consumption_per_week?: string;
  history_diabetes?: string;
  diabetes_treatment?: string;
  colorectal_cancer_risk_factors?: string;
  
  // 위장 질환 관련
  patient_history_of_gastrointestinal_disorder?: string;
  gastrointestinal_disorder_diagnosed?: string;
  prior_synchronous_colon_rectal_tumours?: string;
  history_prior_colon_polyps?: string;
  previous_current_barretts_oesophagus?: string;
  reflux_disease_prior?: string;
  reflux_disease_treatment?: string;
  
  // 감염/특수 질환
  h_pylori_infection?: string;
  history_clinical_chronic_pancreatitis?: string;
  
  // 종양 병기/등급
  tumour_grade?: string;
  tnm_integrated?: string;
  tnm_t?: string;
  tnm_n?: string;
  tnm_m?: string;
  clinical_staging?: string;
  metastasis_at_diagnosis?: string;
  metastatic_site_at_diagnosis?: string;
  
  // 샘플 처리/시술
  method_procurement?: string;
  sample_treatment?: string;
  sample_treatment_details?: string;
  radiation_type?: string;
  prior_therapy_outcome?: string;
  
  // 병리학적 특징
  tumour_regression_score?: string;
  colon_polyps_present_at_time_of_tissue_collection?: string;
  venous_invasion_present?: string;
  venous_invasion_type?: string;
  lymphatic_invasion_present?: string;
  perineural_invasion_present?: string;
  gastric_oesophageal_junction?: string;
  barretts_goblet_cells_present?: string;
  oesophageal_columnar_metaplasia?: string;
  goblet_cells_oesophageal_columnar_mucosa?: string;
  degree_of_dysplasia_non_cancerous_oesophageal_columnar_mucosa?: string;
  peripancreatic_lymph_nodes_tested_surgery?: string;
  number_positive_peripancreatic_lymph_nodes?: number;
  
  // 분자 마커/IHC
  mlh1_expression_by_ihc?: string;
  mlh1_promoter_methylation_status?: string;
  msh2_expression_by_ihc?: string;
  pms2_expression_by_ihc?: string;
  msh6_expression_by_ihc?: string;
  braf_mutation_identified?: string;
  braf_expression_by_ihc?: string;
  pik3ca_mutation_identified?: string;
  pten_expression_by_ihc?: string;
  pten_mutation_identified?: string;
  kras_mutation_identified?: string;
  mismatch_repair_status?: string;
  
  // 기타
  preoperative_ce_alevel?: string;
}

interface ModelListResponse {
  cell_line_list: ModelList[];
}

export interface iModelListState {
  response: ModelListResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: iModelListState = {
  response: null,
  loading: false,
  error: null,
};

// CSV 라인 파싱 함수 (따옴표 안의 쉼표 처리)
const parseLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === ',' && !insideQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  
  return result;
};

// CSV를 객체 배열로 파싱
const parseCSVToObjects = (text: string): ModelList[] => {
  const lines = text.split('\n').filter(line => line.trim());
  
  if (lines.length === 0) {
    console.warn('⚠️ CSV 파일이 비어있습니다.');
    return [];
  }
  
  const headers = parseLine(lines[0]);
  const getColumnIndex = (columnName: string) => headers.indexOf(columnName);
  
  // 모든 컬럼 인덱스 정의
  const indices = {
    model_id: getColumnIndex('model_id'),
    sample_id: getColumnIndex('sample_id'),
    patient_id: getColumnIndex('patient_id'),
    parent_id: getColumnIndex('parent_id'),
    model_name: getColumnIndex('model_name'),
    synonyms: getColumnIndex('synonyms'),
    tissue: getColumnIndex('tissue'),
    cancer_type: getColumnIndex('cancer_type'),
    cancer_type_ncit_id: getColumnIndex('cancer_type_ncit_id'),
    tissue_status: getColumnIndex('tissue_status'),
    sample_site: getColumnIndex('sample_site'),
    cancer_type_detail: getColumnIndex('cancer_type_detail'),
    model_type: getColumnIndex('model_type'),
    growth_properties: getColumnIndex('growth_properties'),
    model_treatment: getColumnIndex('model_treatment'),
    sampling_day: getColumnIndex('sampling_day'),
    sampling_month: getColumnIndex('sampling_month'),
    sampling_year: getColumnIndex('sampling_year'),
    doi: getColumnIndex('doi'),
    pmed: getColumnIndex('pmed'),
    msi_status: getColumnIndex('msi_status'),
    ploidy_snp6: getColumnIndex('ploidy_snp6'),
    ploidy_wes: getColumnIndex('ploidy_wes'),
    ploidy_wgs: getColumnIndex('ploidy_wgs'),
    mutational_burden: getColumnIndex('mutational_burden'),
    model_comments: getColumnIndex('model_comments'),
    model_relations_comment: getColumnIndex('model_relations_comment'),
    COSMIC_ID: getColumnIndex('COSMIC_ID'),
    BROAD_ID: getColumnIndex('BROAD_ID'),
    CCLE_ID: getColumnIndex('CCLE_ID'),
    RRID: getColumnIndex('RRID'),
    HCMI: getColumnIndex('HCMI'),
    suppliers: getColumnIndex('suppliers'),
    supplier: getColumnIndex('supplier'),
    cat_number: getColumnIndex('cat_number'),
    species: getColumnIndex('species'),
    gender: getColumnIndex('gender'),
    ethnicity: getColumnIndex('ethnicity'),
    age_at_sampling: getColumnIndex('age_at_sampling'),
    family_history_of_cancer: getColumnIndex('family_history_of_cancer'),
    prior_same_malignancy: getColumnIndex('prior_same_malignancy'),
    prior_other_malignancy: getColumnIndex('prior_other_malignancy'),
    smoking_status: getColumnIndex('smoking_status'),
    alcohol_exposure_intensity: getColumnIndex('alcohol_exposure_intensity'),
    alcohol_consumption_per_week: getColumnIndex('alcohol_consumption_per_week'),
    history_diabetes: getColumnIndex('history_diabetes'),
    diabetes_treatment: getColumnIndex('diabetes_treatment'),
    colorectal_cancer_risk_factors: getColumnIndex('colorectal_cancer_risk_factors'),
    patient_history_of_gastrointestinal_disorder: getColumnIndex('patient_history_of_gastrointestinal_disorder'),
    gastrointestinal_disorder_diagnosed: getColumnIndex('gastrointestinal_disorder_diagnosed'),
    prior_synchronous_colon_rectal_tumours: getColumnIndex('prior_synchronous_colon_rectal_tumours'),
    history_prior_colon_polyps: getColumnIndex('history_prior_colon_polyps'),
    previous_current_barretts_oesophagus: getColumnIndex('previous_current_barretts_oesophagus'),
    reflux_disease_prior: getColumnIndex('reflux_disease_prior'),
    reflux_disease_treatment: getColumnIndex('reflux_disease_treatment'),
    h_pylori_infection: getColumnIndex('h_pylori_infection'),
    history_clinical_chronic_pancreatitis: getColumnIndex('history_clinical_chronic_pancreatitis'),
    tumour_grade: getColumnIndex('tumour_grade'),
    tnm_integrated: getColumnIndex('tnm_integrated'),
    tnm_t: getColumnIndex('tnm_t'),
    tnm_n: getColumnIndex('tnm_n'),
    tnm_m: getColumnIndex('tnm_m'),
    clinical_staging: getColumnIndex('clinical_staging'),
    metastasis_at_diagnosis: getColumnIndex('metastasis_at_diagnosis'),
    metastatic_site_at_diagnosis: getColumnIndex('metastatic_site_at_diagnosis'),
    method_procurement: getColumnIndex('method_procurement'),
    sample_treatment: getColumnIndex('sample_treatment'),
    sample_treatment_details: getColumnIndex('sample_treatment_details'),
    radiation_type: getColumnIndex('radiation_type'),
    prior_therapy_outcome: getColumnIndex('prior_therapy_outcome'),
    tumour_regression_score: getColumnIndex('tumour_regression_score'),
    colon_polyps_present_at_time_of_tissue_collection: getColumnIndex('colon_polyps_present_at_time_of_tissue_collection'),
    venous_invasion_present: getColumnIndex('venous_invasion_present'),
    venous_invasion_type: getColumnIndex('venous_invasion_type'),
    lymphatic_invasion_present: getColumnIndex('lymphatic_invasion_present'),
    perineural_invasion_present: getColumnIndex('perineural_invasion_present'),
    gastric_oesophageal_junction: getColumnIndex('gastric_oesophageal_junction'),
    barretts_goblet_cells_present: getColumnIndex('barretts_goblet_cells_present'),
    oesophageal_columnar_metaplasia: getColumnIndex('oesophageal_columnar_metaplasia'),
    goblet_cells_oesophageal_columnar_mucosa: getColumnIndex('goblet_cells_oesophageal_columnar_mucosa'),
    degree_of_dysplasia_non_cancerous_oesophageal_columnar_mucosa: getColumnIndex('degree_of_dysplasia_non_cancerous_oesophageal_columnar_mucosa'),
    peripancreatic_lymph_nodes_tested_surgery: getColumnIndex('peripancreatic_lymph_nodes_tested_surgery'),
    number_positive_peripancreatic_lymph_nodes: getColumnIndex('number_positive_peripancreatic_lymph_nodes'),
    mlh1_expression_by_ihc: getColumnIndex('mlh1_expression_by_ihc'),
    mlh1_promoter_methylation_status: getColumnIndex('mlh1_promoter_methylation_status'),
    msh2_expression_by_ihc: getColumnIndex('msh2_expression_by_ihc'),
    pms2_expression_by_ihc: getColumnIndex('pms2_expression_by_ihc'),
    msh6_expression_by_ihc: getColumnIndex('msh6_expression_by_ihc'),
    braf_mutation_identified: getColumnIndex('braf_mutation_identified'),
    braf_expression_by_ihc: getColumnIndex('braf_expression_by_ihc'),
    pik3ca_mutation_identified: getColumnIndex('pik3ca_mutation_identified'),
    pten_expression_by_ihc: getColumnIndex('pten_expression_by_ihc'),
    pten_mutation_identified: getColumnIndex('pten_mutation_identified'),
    kras_mutation_identified: getColumnIndex('kras_mutation_identified'),
    mismatch_repair_status: getColumnIndex('mismatch_repair_status'),
    preoperative_ce_alevel: getColumnIndex('preoperative_ce_alevel'),
  };
  
  // 데이터 파싱
  const dataList = lines
    .slice(1)
    .map((line, index) => {
      const values = parseLine(line);
      
      const item: ModelList = {
        model_id: indices.model_id !== -1 ? values[indices.model_id] : undefined,
        sample_id: indices.sample_id !== -1 ? values[indices.sample_id] : undefined,
        patient_id: indices.patient_id !== -1 ? values[indices.patient_id] : undefined,
        parent_id: indices.parent_id !== -1 ? values[indices.parent_id] : undefined,
        model_name: indices.model_name !== -1 ? values[indices.model_name] : undefined,
        synonyms: indices.synonyms !== -1 ? values[indices.synonyms] : undefined,
        tissue: indices.tissue !== -1 ? values[indices.tissue] : undefined,
        cancer_type: indices.cancer_type !== -1 ? values[indices.cancer_type] : undefined,
        cancer_type_ncit_id: indices.cancer_type_ncit_id !== -1 ? values[indices.cancer_type_ncit_id] : undefined,
        tissue_status: indices.tissue_status !== -1 ? values[indices.tissue_status] : undefined,
        sample_site: indices.sample_site !== -1 ? values[indices.sample_site] : undefined,
        cancer_type_detail: indices.cancer_type_detail !== -1 ? values[indices.cancer_type_detail] : undefined,
        model_type: indices.model_type !== -1 ? values[indices.model_type] : undefined,
        growth_properties: indices.growth_properties !== -1 ? values[indices.growth_properties] : undefined,
        model_treatment: indices.model_treatment !== -1 ? values[indices.model_treatment] : undefined,
        sampling_day: indices.sampling_day !== -1 ? values[indices.sampling_day] : undefined,
        sampling_month: indices.sampling_month !== -1 ? values[indices.sampling_month] : undefined,
        sampling_year: indices.sampling_year !== -1 && values[indices.sampling_year] && !isNaN(parseInt(values[indices.sampling_year])) 
          ? parseInt(values[indices.sampling_year]) 
          : undefined,
        doi: indices.doi !== -1 ? values[indices.doi] : undefined,
        pmed: indices.pmed !== -1 ? values[indices.pmed] : undefined,
        msi_status: indices.msi_status !== -1 ? values[indices.msi_status] : undefined,
        ploidy_snp6: indices.ploidy_snp6 !== -1 ? values[indices.ploidy_snp6] : undefined,
        ploidy_wes: indices.ploidy_wes !== -1 ? values[indices.ploidy_wes] : undefined,
        ploidy_wgs: indices.ploidy_wgs !== -1 ? values[indices.ploidy_wgs] : undefined,
        mutational_burden: indices.mutational_burden !== -1 ? values[indices.mutational_burden] : undefined,
        model_comments: indices.model_comments !== -1 ? values[indices.model_comments] : undefined,
        model_relations_comment: indices.model_relations_comment !== -1 ? values[indices.model_relations_comment] : undefined,
        COSMIC_ID: indices.COSMIC_ID !== -1 ? values[indices.COSMIC_ID] : undefined,
        BROAD_ID: indices.BROAD_ID !== -1 ? values[indices.BROAD_ID] : undefined,
        CCLE_ID: indices.CCLE_ID !== -1 ? values[indices.CCLE_ID] : undefined,
        RRID: indices.RRID !== -1 ? values[indices.RRID] : undefined,
        HCMI: indices.HCMI !== -1 ? values[indices.HCMI] : undefined,
        suppliers: indices.suppliers !== -1 ? values[indices.suppliers] : undefined,
        supplier: indices.supplier !== -1 ? values[indices.supplier] : undefined,
        cat_number: indices.cat_number !== -1 ? values[indices.cat_number] : undefined,
        species: indices.species !== -1 ? values[indices.species] : undefined,
        gender: indices.gender !== -1 ? values[indices.gender] : undefined,
        ethnicity: indices.ethnicity !== -1 ? values[indices.ethnicity] : undefined,
        age_at_sampling: indices.age_at_sampling !== -1 && values[indices.age_at_sampling] && !isNaN(parseInt(values[indices.age_at_sampling])) 
          ? parseInt(values[indices.age_at_sampling]) 
          : undefined,
        family_history_of_cancer: indices.family_history_of_cancer !== -1 ? values[indices.family_history_of_cancer] : undefined,
        prior_same_malignancy: indices.prior_same_malignancy !== -1 ? values[indices.prior_same_malignancy] : undefined,
        prior_other_malignancy: indices.prior_other_malignancy !== -1 ? values[indices.prior_other_malignancy] : undefined,
        smoking_status: indices.smoking_status !== -1 ? values[indices.smoking_status] : undefined,
        alcohol_exposure_intensity: indices.alcohol_exposure_intensity !== -1 ? values[indices.alcohol_exposure_intensity] : undefined,
        alcohol_consumption_per_week: indices.alcohol_consumption_per_week !== -1 ? values[indices.alcohol_consumption_per_week] : undefined,
        history_diabetes: indices.history_diabetes !== -1 ? values[indices.history_diabetes] : undefined,
        diabetes_treatment: indices.diabetes_treatment !== -1 ? values[indices.diabetes_treatment] : undefined,
        colorectal_cancer_risk_factors: indices.colorectal_cancer_risk_factors !== -1 ? values[indices.colorectal_cancer_risk_factors] : undefined,
        patient_history_of_gastrointestinal_disorder: indices.patient_history_of_gastrointestinal_disorder !== -1 ? values[indices.patient_history_of_gastrointestinal_disorder] : undefined,
        gastrointestinal_disorder_diagnosed: indices.gastrointestinal_disorder_diagnosed !== -1 ? values[indices.gastrointestinal_disorder_diagnosed] : undefined,
        prior_synchronous_colon_rectal_tumours: indices.prior_synchronous_colon_rectal_tumours !== -1 ? values[indices.prior_synchronous_colon_rectal_tumours] : undefined,
        history_prior_colon_polyps: indices.history_prior_colon_polyps !== -1 ? values[indices.history_prior_colon_polyps] : undefined,
        previous_current_barretts_oesophagus: indices.previous_current_barretts_oesophagus !== -1 ? values[indices.previous_current_barretts_oesophagus] : undefined,
        reflux_disease_prior: indices.reflux_disease_prior !== -1 ? values[indices.reflux_disease_prior] : undefined,
        reflux_disease_treatment: indices.reflux_disease_treatment !== -1 ? values[indices.reflux_disease_treatment] : undefined,
        h_pylori_infection: indices.h_pylori_infection !== -1 ? values[indices.h_pylori_infection] : undefined,
        history_clinical_chronic_pancreatitis: indices.history_clinical_chronic_pancreatitis !== -1 ? values[indices.history_clinical_chronic_pancreatitis] : undefined,
        tumour_grade: indices.tumour_grade !== -1 ? values[indices.tumour_grade] : undefined,
        tnm_integrated: indices.tnm_integrated !== -1 ? values[indices.tnm_integrated] : undefined,
        tnm_t: indices.tnm_t !== -1 ? values[indices.tnm_t] : undefined,
        tnm_n: indices.tnm_n !== -1 ? values[indices.tnm_n] : undefined,
        tnm_m: indices.tnm_m !== -1 ? values[indices.tnm_m] : undefined,
        clinical_staging: indices.clinical_staging !== -1 ? values[indices.clinical_staging] : undefined,
        metastasis_at_diagnosis: indices.metastasis_at_diagnosis !== -1 ? values[indices.metastasis_at_diagnosis] : undefined,
        metastatic_site_at_diagnosis: indices.metastatic_site_at_diagnosis !== -1 ? values[indices.metastatic_site_at_diagnosis] : undefined,
        method_procurement: indices.method_procurement !== -1 ? values[indices.method_procurement] : undefined,
        sample_treatment: indices.sample_treatment !== -1 ? values[indices.sample_treatment] : undefined,
        sample_treatment_details: indices.sample_treatment_details !== -1 ? values[indices.sample_treatment_details] : undefined,
        radiation_type: indices.radiation_type !== -1 ? values[indices.radiation_type] : undefined,
        prior_therapy_outcome: indices.prior_therapy_outcome !== -1 ? values[indices.prior_therapy_outcome] : undefined,
        tumour_regression_score: indices.tumour_regression_score !== -1 ? values[indices.tumour_regression_score] : undefined,
        colon_polyps_present_at_time_of_tissue_collection: indices.colon_polyps_present_at_time_of_tissue_collection !== -1 ? values[indices.colon_polyps_present_at_time_of_tissue_collection] : undefined,
        venous_invasion_present: indices.venous_invasion_present !== -1 ? values[indices.venous_invasion_present] : undefined,
        venous_invasion_type: indices.venous_invasion_type !== -1 ? values[indices.venous_invasion_type] : undefined,
        lymphatic_invasion_present: indices.lymphatic_invasion_present !== -1 ? values[indices.lymphatic_invasion_present] : undefined,
        perineural_invasion_present: indices.perineural_invasion_present !== -1 ? values[indices.perineural_invasion_present] : undefined,
        gastric_oesophageal_junction: indices.gastric_oesophageal_junction !== -1 ? values[indices.gastric_oesophageal_junction] : undefined,
        barretts_goblet_cells_present: indices.barretts_goblet_cells_present !== -1 ? values[indices.barretts_goblet_cells_present] : undefined,
        oesophageal_columnar_metaplasia: indices.oesophageal_columnar_metaplasia !== -1 ? values[indices.oesophageal_columnar_metaplasia] : undefined,
        goblet_cells_oesophageal_columnar_mucosa: indices.goblet_cells_oesophageal_columnar_mucosa !== -1 ? values[indices.goblet_cells_oesophageal_columnar_mucosa] : undefined,
        degree_of_dysplasia_non_cancerous_oesophageal_columnar_mucosa: indices.degree_of_dysplasia_non_cancerous_oesophageal_columnar_mucosa !== -1 ? values[indices.degree_of_dysplasia_non_cancerous_oesophageal_columnar_mucosa] : undefined,
        peripancreatic_lymph_nodes_tested_surgery: indices.peripancreatic_lymph_nodes_tested_surgery !== -1 ? values[indices.peripancreatic_lymph_nodes_tested_surgery] : undefined,
        number_positive_peripancreatic_lymph_nodes: indices.number_positive_peripancreatic_lymph_nodes !== -1 && values[indices.number_positive_peripancreatic_lymph_nodes] && !isNaN(parseInt(values[indices.number_positive_peripancreatic_lymph_nodes])) 
          ? parseInt(values[indices.number_positive_peripancreatic_lymph_nodes]) 
          : undefined,
        mlh1_expression_by_ihc: indices.mlh1_expression_by_ihc !== -1 ? values[indices.mlh1_expression_by_ihc] : undefined,
        mlh1_promoter_methylation_status: indices.mlh1_promoter_methylation_status !== -1 ? values[indices.mlh1_promoter_methylation_status] : undefined,
        msh2_expression_by_ihc: indices.msh2_expression_by_ihc !== -1 ? values[indices.msh2_expression_by_ihc] : undefined,
        pms2_expression_by_ihc: indices.pms2_expression_by_ihc !== -1 ? values[indices.pms2_expression_by_ihc] : undefined,
        msh6_expression_by_ihc: indices.msh6_expression_by_ihc !== -1 ? values[indices.msh6_expression_by_ihc] : undefined,
        braf_mutation_identified: indices.braf_mutation_identified !== -1 ? values[indices.braf_mutation_identified] : undefined,
        braf_expression_by_ihc: indices.braf_expression_by_ihc !== -1 ? values[indices.braf_expression_by_ihc] : undefined,
        pik3ca_mutation_identified: indices.pik3ca_mutation_identified !== -1 ? values[indices.pik3ca_mutation_identified] : undefined,
        pten_expression_by_ihc: indices.pten_expression_by_ihc !== -1 ? values[indices.pten_expression_by_ihc] : undefined,
        pten_mutation_identified: indices.pten_mutation_identified !== -1 ? values[indices.pten_mutation_identified] : undefined,
        kras_mutation_identified: indices.kras_mutation_identified !== -1 ? values[indices.kras_mutation_identified] : undefined,
        mismatch_repair_status: indices.mismatch_repair_status !== -1 ? values[indices.mismatch_repair_status] : undefined,
        preoperative_ce_alevel: indices.preoperative_ce_alevel !== -1 ? values[indices.preoperative_ce_alevel] : undefined,
      };
      
      
      return item;
    })
    .filter(item => item.model_id); // model_id가 있는 유효한 데이터만
  
  return dataList;
};

// CSV에서 Cell Line 리스트 가져오기
export const getModelListDataAPI = createAsyncThunk<ModelListResponse>(
  'cellLine/getFromCSV',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/data/model_list_20250630.csv');
      
      if (!response.ok) {
        throw new Error(`CSV 파일 로드 실패: ${response.status} ${response.statusText}`);
      }
      
      const text = await response.text();
      const cellLineList = parseCSVToObjects(text);
      
      if (cellLineList.length === 0) {
        throw new Error('파싱된 데이터가 없습니다.');
      }
      
      return { cell_line_list: cellLineList };
    } catch (error) {
      console.error('Cell Line CSV 로딩 실패:', error);
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load cell line list'));
    }
  }
);

const modelListSlice = createSlice({
  name: 'modelList',
  initialState,
  reducers: {
    clearModelList: (state) => {
      state.response = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getModelListDataAPI.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getModelListDataAPI.fulfilled, (state, action: PayloadAction<ModelListResponse>) => {
        state.loading = false;
        state.response = action.payload;
      })
      .addCase(getModelListDataAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to load cell line list';
        console.error('Cell Line Redux 에러:', action.payload);
      });
  },
});

export const { clearModelList } = modelListSlice.actions;
export default modelListSlice.reducer;